import * as DOMUtils from "history/DOMUtils";
import type { ColourType, UpholsteryType } from "../../../../common-deprecated/types/CommonTypes";
import type { CodConfigurationType } from "../types/CodType";
import CarDataConsolidator from "../../../shared-logic/utils/classes/CarDataConsolidator";
import type { BuildAndBuyStateType } from "../redux/store";
import type { FetchSpecsEquipmentDataType } from "../../../shared-logic/server/data/FetchSpecifications";
import type { SortedSpecType } from "../../../../common-deprecated/types/SpecTypes";
import type { FetchFnReturnType } from "../../../../common-deprecated/utils/backendFetch";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import type { ItemsType } from "../../../../common-deprecated/redux/typeHelpers";
import { formatStandardEquipmentList } from "../../../../common-deprecated/utils";
import { getCarImageFromConfig } from "../../../shared-logic/utils/typedUtils";

export const getCodBuildURLs = (
    commonSettings: CommonSettingsType,
): { url: string; search: string; apiBasePath: string; apiEndPoint: string } => {
    const apiBasePath = `${commonSettings.apiEndpoint}/cod/data/{COUNTRY}/{LANGUAGE}`;
    return {
        url: window.location.href,
        search: window.location.search,
        apiBasePath,
        apiEndPoint: `${apiBasePath}${window.location.search}`,
    };
};

export const getCodConfiguration = ({
    car,
    basket,
    commonSettings,
}: {
    car: CarType;
    basket: BasketType;
    commonSettings: CommonSettingsType;
}): CodConfigurationType => {
    const { upholstery, wheel, winterWheel, extras, packs } = basket;
    const { model, submodel, body, grade, motorization, price, name } = car;
    const { culture } = commonSettings;

    // Ignores default colour.upholsteries when a pack upholstery is selected
    const packUpholsteryIds = CarDataConsolidator.getBasketPackUpholsteries(basket.packs, false, basket.colour.id).map(
        ({ id }) => id,
    );
    const colour =
        packUpholsteryIds.length > 0
            ? {
                  ...basket.colour,
                  upholsteries: packUpholsteryIds,
                  // Currently neither of these properties are used in COD so just filling them with all upholsteries for now
                  upholsteriesCarConfigurator: packUpholsteryIds,
                  upholsteryOnlineAvailability: packUpholsteryIds,
              }
            : basket.colour;

    return {
        carId: car.id,
        carName: name,
        model,
        submodel,
        body,
        grade,
        motorization,
        colour,
        upholstery,
        wheel,
        winterWheel,
        extras,
        packs,
        totalPrice: basket.total,
        basePrice: price,
        settings: {
            url: DOMUtils.canUseDOM ? window.location.href : "",
            ccisPath: commonSettings.ccisPath,
            // Culture is used in combination with Globalize to format prices, originates from the T1.settings
            culture: {
                ...culture,
                // Empty the messages (= translations/labels of the build), this is not required for the cod
                messages: {},
            },
            currencyMultiplier: commonSettings.currencyMultiplier,
            imageURL: getCarImageFromConfig(
                commonSettings,
                "{VIEW}",
                "{FRAME}",
                model.id,
                car.id,
                {
                    // @ts-ignore
                    type: "{TYPE}",
                    bgColour: "{BACKGROUNDCOLOUR}",
                    scaleMode: "{SCALEMODE}",
                    padding: "{PADDING}",
                    width: "{WIDTH}",
                    height: "{HEIGHT}",
                    imageQuality: "{IMAGEQUALITY}",
                },
                colour,
                upholstery,
                wheel,
                packs,
                extras,
                true,
            ),
        },
    };
};

type ConfigurablesForConfigType = {
    basket: BasketType;
    items: ItemsType;
};

const getConfigurableForConfiguration = (
    { basket, items }: ConfigurablesForConfigType,
    configurable: "colours" | "upholsteries",
): ColourType[] | UpholsteryType[] => {
    const originalList = (items as any)[configurable];
    const { extras, packs, wheel } = basket;
    // Create an array of all items which have availability filled in
    const availabilities = [...extras, ...packs, wheel].filter((item) => !!item.availability[configurable].length);
    // If no availabilities are found within the selected items, just return all colours/upholsteries!
    if (!availabilities.length) return originalList;
    // Join the array of available colours/upholsteries (using Object for duplicates)
    const totalAvailableItems = availabilities.reduce((acc: any, item) => {
        item.availability[configurable].forEach((av: any) => {
            acc[av] = true;
        });
        return acc;
    }, {});
    // Filter out colours/upholsteries which are not shared among the availableItems
    const availableItems = Object.keys(totalAvailableItems).filter((av) => {
        let match = true;
        availabilities.forEach((item) => {
            if (!item.availability[configurable].find((iav: any) => iav === av)) {
                match = false;
            }
        });
        return match;
    });
    // Create an Array of keys
    return originalList.filter((item: any) => availableItems.find((ac) => ac === item.id));
};

export const getColoursForConfiguration = (state: ConfigurablesForConfigType): ColourType[] => {
    const packColours = CarDataConsolidator.getBasketPackColours(state.basket.packs);
    const packUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(
        state.basket.packs,
        false,
        state.basket.colour.id,
    ) as UpholsteryType[];

    const colours = packColours.length
        ? packColours
        : (getConfigurableForConfiguration(state, "colours") as ColourType[]);

    // Ignores default colour.upholsteries when a pack upholstery is selected
    // Normally colors.upholstery is ignored when a pack contains an upholstery, but COD doesn't
    if (packUpholsteries.length > 0) {
        const packUpholsteryIds = packUpholsteries.map((upholstery) => upholstery.id);
        return colours.map((color) => ({
            ...color,
            upholsteries: packUpholsteryIds,
            // Currently neither of these properties are used in COD so just filling them with all upholsteries for now
            upholsteriesCarConfigurator: packUpholsteryIds,
            upholsteryOnlineAvailability: packUpholsteryIds,
        }));
    }

    return colours;
};

export const getUpholsteriesForConfiguration = (state: ConfigurablesForConfigType): UpholsteryType[] => {
    const packUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(
        state.basket.packs,
        false,
        state.basket.colour.id,
    ) as UpholsteryType[];
    return packUpholsteries.length
        ? packUpholsteries
        : (getConfigurableForConfiguration(state, "upholsteries") as UpholsteryType[]);
};

export type CodDataType = {
    configuration: CodConfigurationType;
    colours: ColourType[];
    upholsteries: UpholsteryType[];
    /**
     * This typing got broken during an upgrade of the fetchers, and it was fixed inside the CoD app without us knowing, instead of here.
     * Keeping this for now as fixing this would mean aligning on a slow ios app release.
     * See: https://github.com/marlon-be/tme-car-on-display-app/blob/cbde734063f586d99b84c88e34064700709f6922/TmeCarOnDisplay/src/app/reducers/types.js#L193
     */
    specs: FetchFnReturnType<FetchSpecsEquipmentDataType> & {
        standardEquipment: SortedSpecType[];
    };
};

export const getCodData = (
    state: BuildAndBuyStateType,
    specs: FetchFnReturnType<FetchSpecsEquipmentDataType>,
): CodDataType => {
    const { basket, items, car, commonSettings } = state;

    const configuration = getCodConfiguration({ car, basket, commonSettings });
    const colours = getColoursForConfiguration({ basket, items });
    const upholsteries = getUpholsteriesForConfiguration({ basket, items });

    // Filter out standard equipment excluded by other selected equipment + resolve colour/upholstery availability
    const standardEquipment = CarDataConsolidator.filterStandardEquipmentSpecs(
        basket,
        items,
        specs.fetchResult.standardEquipment,
    ).filter((extra) => CarDataConsolidator.isAvailable(extra, basket.colour, basket.upholstery).available);

    return {
        configuration,
        colours,
        upholsteries,
        specs: { ...specs, standardEquipment: formatStandardEquipmentList(standardEquipment) },
    };
};
